import { MessageCircle } from "lucide-react";

import { iconForName } from "@/components/artifacts/iconForName.ts";
import { clickableCardClass } from "@/components/threads/shared.tsx";
import { formatThreadStatus } from "@/components/threads/util.ts";
import { cn } from "@/lib/utils.ts";
import { artifactRowMeta, artifactRowTitle } from "@/shared/artifactLibraryRow.ts";
import { formatLibraryDate } from "@/shared/libraryDate.ts";
import type { ArtifactLibraryItem } from "@/types/artifactLibrary.ts";

/** Relative-then-absolute date from the item's most recent timestamp. */
const formatDate = (item: ArtifactLibraryItem): string =>
  formatLibraryDate(
    item.itemKind === "thread" ? item.updatedAt : (item.updatedAt ?? item.createdAt),
  );

type ItemViewProps = {
  item: ArtifactLibraryItem;
  onSelect: (item: ArtifactLibraryItem) => void;
};

/** Shared click + keyboard handlers so a row/card behaves like a button. */
const selectHandlers = (item: ArtifactLibraryItem, onSelect: ItemViewProps["onSelect"]) => ({
  role: "button" as const,
  tabIndex: 0,
  onClick: () => onSelect(item),
  onKeyDown: (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(item);
    }
  },
});

/**
 * The icon, primary label, and secondary line a row/card shows, resolved for
 * either item kind so the two layouts below stay layout-only.
 */
const itemChrome = (item: ArtifactLibraryItem) => {
  if (item.itemKind === "thread") {
    // `formatThreadStatus` is the same map the thread cards use, so a thread
    // reads identically in the Library and in the list it came from.
    const statusLabel = formatThreadStatus(item.status);
    return {
      Icon: MessageCircle,
      title: item.title,
      // The snippet is why this thread matched, so it earns the secondary line
      // ahead of the status when there is one.
      subtitle: item.snippet ?? `Thread · ${statusLabel}`,
      typeTitle: "Thread",
      statusLabel,
    };
  }
  const meta = artifactRowMeta(item);
  if (item.itemKind === "sharedArtifact") {
    // Who shared it displaces the status. A status label is about work still in
    // flight on something you own; on a read-only item from someone else's
    // account, the sharer is the fact that orients you.
    const sharedBy = `Shared by ${item.sharedBy.name}`;
    return {
      Icon: iconForName(meta.iconName),
      title: artifactRowTitle(item),
      subtitle: `${meta.typeTitle} · ${sharedBy}`,
      typeTitle: meta.typeTitle,
      statusLabel: sharedBy,
    };
  }
  return {
    Icon: iconForName(meta.iconName),
    title: artifactRowTitle(item),
    subtitle: meta.typeTitle + (meta.statusLabel ? ` · ${meta.statusLabel}` : ""),
    typeTitle: meta.typeTitle,
    statusLabel: meta.statusLabel,
  };
};

/** Dense list row: icon, title, type · status (or match snippet), date. */
export const ArtifactLibraryRow = ({ item, onSelect }: ItemViewProps) => {
  const { Icon, title, subtitle } = itemChrome(item);
  return (
    <div
      {...selectHandlers(item, onSelect)}
      className={cn(clickableCardClass, "flex items-center gap-3")}
    >
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{title}</div>
        <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
      </div>
      <div className="shrink-0 text-xs text-muted-foreground">{formatDate(item)}</div>
    </div>
  );
};

/** Grid card: icon + type on top, title, status + date at the bottom. */
export const ArtifactLibraryCard = ({ item, onSelect }: ItemViewProps) => {
  const { Icon, title, typeTitle, statusLabel } = itemChrome(item);
  return (
    <div
      {...selectHandlers(item, onSelect)}
      className={cn(clickableCardClass, "flex h-full flex-col gap-2")}
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="size-4 shrink-0" />
        <span className="truncate">{typeTitle}</span>
      </div>
      <div className="line-clamp-2 flex-1 text-sm font-medium">{title}</div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="truncate">{statusLabel}</span>
        <span className="shrink-0">{formatDate(item)}</span>
      </div>
    </div>
  );
};
